import { withErrorBoundary } from "@sentry/react";
import { useLocation } from "react-router-dom";
import {
  ArtifactComponentFragmentFragment,
  BasicGoalArtifactComponentFragmentFragment,
  GoalOverviewRelatedEntityGoalFragmentFragment,
  GoalState,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import { AlignedGoalType } from "@apps/goal-alignment/use-fetch-alignment-goals";
import GoalProgress from "@apps/goal-progress/goal-progress";
import { GoalProgressView } from "@apps/goal-progress/goal-progress-constants";
import useLabel from "@apps/use-label/use-label";
import Avatar from "@components/avatar/avatar";
import { ButtonSize } from "@components/button/button-constants";
import DraftLabel from "@components/draft-label/draft-label";
import Error from "@components/error/error";
import GoalVisibilityIcon from "@components/goal-visibility-icon/goal-visibility-icon";
import Link from "@components/link/link";
import TimeAgoCustom from "@components/time-ago/time-ago";
import { getPrivateGoalTitle } from "@helpers/constants";
import { classNames } from "@helpers/css";
import {
  getUrl,
  isPrivateBasicGoalArtifactNode,
  toWithBackground,
} from "@helpers/helpers";

import ArtifactDropdownMenu from "./artifact-dropdown-menu";
import { getRecognitionRecipientTitle } from "./artifact-helpers";
import ActionItemAssignee from "./components/artifact-assignee";
import ArtifactCreated from "./components/artifact-created";
import ArtifactDueDate from "./components/artifact-due-date";
import ArtifactIcon from "./components/artifact-icon";
import GoalParticipants from "./components/goal-participants";
import ArtifactRecognitionRecipients from "./components/recognition-recipients";

const ArtifactComponent = ({
  artifact,
  size = 5,
  urlPrefix = "",
  hideActionDropdown = false,
  showTitleOnlyOnOneLine = false,
  goalProgressView = GoalProgressView.pillAndProgressText,
  compact = false,
  linkWithoutBackground = false,
}: {
  artifact:
    | AlignedGoalType
    | ({
        __typename: "BasicGoalArtifactNode";
      } & BasicGoalArtifactComponentFragmentFragment)
    | GoalOverviewRelatedEntityGoalFragmentFragment
    | ArtifactComponentFragmentFragment;
  urlPrefix?: string;
  size?: number;
  hideActionDropdown?: boolean;
  showTitleOnlyOnOneLine?: boolean;
  goalProgressView?: GoalProgressView;
  // When true, the row uses the meeting-page artifact embed sizing: 16x16
  // left icon, 20x20 (small) right-side controls, 16x16 avatars. Used by
  // the Explorer search results.
  compact?: boolean;
  // When true, the title link navigates without stashing a background
  // location, so goals open as their own full page instead of the drawer.
  // Used by the Goals section (e.g. the alignment tree).
  linkWithoutBackground?: boolean;
}) => {
  const effectiveSize = compact ? 4 : size;
  const rightSideButtonSize = compact ? ButtonSize.small : ButtonSize.medium;
  const rightSideAvatarSize = compact ? "4" : "5";
  const location = useLocation<TFLocationState>();
  const isActionItem = artifact.__typename === "ActionItemArtifactNode";
  const isDecision = artifact.__typename === "DecisionArtifactNode";
  const isGoal = artifact.__typename === "GoalArtifactNode";
  const isBasicGoal = artifact.__typename === "BasicGoalArtifactNode";
  const isDocument = artifact.__typename === "DocumentArtifactNode";
  const isRecognition = artifact.__typename === "RecognitionArtifactNode";
  const isRating = artifact.__typename === "RatingArtifactNode";
  const isPrivateBasicGoal = isPrivateBasicGoalArtifactNode(artifact);
  const l = useLabel();
  const title =
    (isDecision
      ? artifact.decision
      : isRating
        ? artifact.rating?.title
        : isRecognition
          ? getRecognitionRecipientTitle(artifact)
          : !isBasicGoal
            ? artifact.title
            : artifact.goalArtifact
              ? artifact.goalArtifact.title
              : getPrivateGoalTitle()) || "";

  const artifactUrl = getUrl({
    artifactId: artifact.id,
    artifactType: artifact.artifactType,
  });

  return (
    <div
      key={artifact.id}
      aria-label={`${l(artifact.artifactType)} container`}
      className="@container/artifact"
    >
      <div key={artifact.id} className="flex items-start gap-1.5 min-w-0">
        <ArtifactIcon
          artifact={artifact}
          size={effectiveSize}
          // 2px top in compact (Explorer) mode lines the icon up with the
          // header text; 1px elsewhere matches the existing larger sizing.
          className={classNames(compact ? "mt-0.5" : "mt-px")}
        />
        <Link
          className={classNames(
            "text-sm text-gray-700 flex-1 break-word group",
            // 1px top margin keeps the title baseline-aligned in the larger
            // (default) sizing; compact (Explorer) rows are tight enough
            // that the same margin pushes the text noticeably down.
            !compact && "mt-px",
            showTitleOnlyOnOneLine ? "truncate" : "line-clamp-2",
          )}
          to={
            linkWithoutBackground
              ? `${urlPrefix}${artifactUrl}`
              : toWithBackground({
                  pathname: `${urlPrefix}${artifactUrl}`,
                  location,
                })
          }
          disabled={isPrivateBasicGoal}
        >
          <span
            className={classNames(
              "no-underline",
              !isPrivateBasicGoal &&
                "group-hover:underline-offset-2 group-hover:underline group-hover:text-blue-600 group-hover:decoration-blue-300",
              isPrivateBasicGoal && "text-gray-400 group-hover:no-underline",
            )}
          >
            {title}
          </span>
          {(((isGoal || isBasicGoal) && artifact.state === GoalState.Draft) ||
            (isDecision && artifact.isDraft)) && (
            <DraftLabel className="ml-1" />
          )}
          {(isGoal || isBasicGoal) && (
            <GoalVisibilityIcon
              showOnlyIfPrivate
              goalVisibility={artifact.goalVisibility}
              className="ml-1 inline-block"
              size="4"
            />
          )}
        </Link>

        <div className="flex items-center gap-2.5">
          {(isActionItem || isGoal || isBasicGoal) && (
            <div className="hidden sm:flex items-center text-xs -mx-1">
              <ArtifactDueDate artifact={artifact} />
            </div>
          )}
          {(isGoal || isBasicGoal) && (
            <GoalProgress
              withPopover={true}
              artifact={artifact}
              goalProgressView={goalProgressView}
            />
          )}
          {isActionItem && (
            <ActionItemAssignee
              artifact={artifact}
              showName={false}
              size={rightSideButtonSize}
            />
          )}
          {(isGoal || isBasicGoal) && <GoalParticipants artifact={artifact} />}
          {isDocument && artifact.updated && (
            <span className="hidden @xl/artifact:block text-gray-500 text-xs tracking-tight">
              <TimeAgoCustom date={artifact.updated} prefix="Last updated: " />
            </span>
          )}
          {isDocument && (
            <Avatar
              user={artifact.creator}
              size={rightSideAvatarSize}
              tooltipPrefix="Created by "
            />
          )}
          {isRecognition && (
            <div className="hidden @xl/artifact:block">
              <ArtifactCreated artifact={artifact} />
            </div>
          )}
          {isRecognition && (
            <ArtifactRecognitionRecipients artifact={artifact} />
          )}

          {!hideActionDropdown && (
            <div className="flex">
              {isBasicGoal && !artifact.goalArtifact ? (
                <div className="w-5 h-5" />
              ) : (
                <ArtifactDropdownMenu
                  artifact={artifact}
                  portal={true}
                  size={rightSideButtonSize}
                  className="hover:bg-black/5 p-0.5"
                />
              )}
            </div>
          )}
        </div>
      </div>

      {isRecognition && (
        <div className="text-sm mt-1 text-gray-500 ml-7">{artifact.title}</div>
      )}
    </div>
  );
};

const ArtifactWithSentry = withErrorBoundary(ArtifactComponent, {
  fallback: <Error description={"The artifact could not be rendered."} />,
});

export default ArtifactWithSentry;
